/**
 * Local activity observation for one tab.
 *
 * **It no longer decides anything.** This class used to own the inactivity
 * timeout: a 30-minute timer per tab which, on firing, cleared the shared
 * `.airclou.com` cookie and signed the whole suite out. Because its clock was a
 * plain in-memory field fed only by *its own* document's events, a tab you left
 * open would fire while you were working in another app and sign you out
 * mid-sentence — and a screen you genuinely walked away from, in an app with no
 * tracker, expired nothing at all. Idle is a property of the person, not of a tab.
 *
 * The clock now lives on the `sessions` row in auth, where every app on every
 * origin feeds one value, and `AppAuthProvider` owns both the input listeners and
 * the renewal. So the logout and the token-refresh machinery are gone from here;
 * what remains is a read-only view of *this* tab's last input, which the debug
 * panel renders.
 *
 * Retained rather than deleted because four apps construct it through their own
 * `sessionManager` and `SessionDebugPanel` reads it. The dependencies it no longer
 * uses are still accepted and ignored, so those call sites keep compiling while
 * they are unwound.
 *
 * Background: apps/auth/docs/session-idle-expiry-plan.md (D-1, D-2, I-12).
 */
const DEFAULT_CONFIG = {
    inactivityTimeoutMs: 30 * 60 * 1000, // 30 minutes
    tokenRefreshBeforeExpiryMs: 5 * 60 * 1000, // Refresh 5 min before expiry
    checkIntervalMs: 60 * 1000, // Check every minute
};
export class ActivityTracker {
    lastActivityTime = Date.now();
    config = DEFAULT_CONFIG;
    dependencies;
    eventListeners = [];
    isActive = false;
    constructor(dependencies) {
        this.dependencies = dependencies;
    }
    /**
     * Initialize activity tracking
     */
    start(config) {
        if (this.isActive) {
            this.log('warn', '⚠️ Activity tracker already running');
            return;
        }
        // Merge config with defaults
        this.config = { ...DEFAULT_CONFIG, ...config };
        this.lastActivityTime = Date.now();
        this.isActive = true;
        // Deliberately no `willLogoutAt`: this tab does not log anyone out any more,
        // and a log line claiming a deadline it cannot enforce is how the next reader
        // concludes the local clock still decides.
        this.log('info', '🎯 Activity tracker observing this tab', { startTime: new Date().toISOString() });
        // Observation only. No inactivity timer and no token refresh: this tab must
        // not expire a session the user may be actively using elsewhere, and renewal
        // belongs to the provider so the two cannot drift.
        this.setupEventListeners();
        this.log('info', '✅ Activity tracker observing input');
    }
    /**
     * Stop activity tracking and clean up
     */
    stop() {
        if (!this.isActive)
            return;
        this.log('info', '🛑 Activity tracker stopped');
        // Remove event listeners
        this.eventListeners.forEach(({ event, handler }) => {
            window.removeEventListener(event, handler);
        });
        this.eventListeners = [];
        this.isActive = false;
    }
    /**
     * Manually record activity (useful for programmatic activity)
     */
    recordActivity() {
        this.lastActivityTime = Date.now();
    }
    /**
     * Get time since last activity in milliseconds
     */
    getInactiveTime() {
        return Date.now() - this.lastActivityTime;
    }
    /**
     * Get current configuration
     */
    getConfig() {
        return { ...this.config };
    }
    /**
     * Set up event listeners for user activity
     */
    setupEventListeners() {
        const activityEvents = [
            'mousedown',
            'mousemove',
            'keypress',
            'scroll',
            'touchstart',
            'click',
        ];
        this.log('info', '👂 Setting up activity event listeners:', activityEvents);
        // Throttle activity updates to avoid excessive calls
        let throttleTimer = null;
        const throttledUpdateActivity = (event) => {
            if (throttleTimer)
                return;
            const previousTime = this.lastActivityTime;
            this.lastActivityTime = Date.now();
            // Notify external listeners about activity
            if (this.dependencies.onActivity) {
                this.dependencies.onActivity();
            }
            // Log activity in development
            if (this.dependencies.isDevelopment && Date.now() - previousTime > 5000) {
                this.log('debug', '✋ Activity detected:', event.type, {
                    timeSinceLastActivity: `${Math.floor((Date.now() - previousTime) / 1000)}s`,
                    newLastActivity: new Date(this.lastActivityTime).toISOString()
                });
            }
            throttleTimer = setTimeout(() => {
                throttleTimer = null;
            }, 1000); // Throttle to max 1 update per second
        };
        // Add event listeners
        activityEvents.forEach((event) => {
            const handler = throttledUpdateActivity;
            window.addEventListener(event, handler, { passive: true });
            this.eventListeners.push({ event, handler });
        });
        this.log('info', `✅ Activity listeners registered: ${this.eventListeners.length} events`);
    }
    /**
     * Internal logging utility that respects environment
     */
    log(level, ...args) {
        // In production, only log warnings and errors
        if (!this.dependencies.isDevelopment && (level === 'debug' || level === 'info')) {
            return;
        }
        const method = level === 'debug' ? 'log' : level;
        console[method](...args);
    }
}
/**
 * Configuration helper to convert minutes/seconds to milliseconds
 */
export const configureActivityTracker = (config) => {
    return {
        inactivityTimeoutMs: (config.inactivityTimeoutMinutes || 30) * 60 * 1000,
        tokenRefreshBeforeExpiryMs: (config.tokenRefreshBeforeExpiryMinutes || 5) * 60 * 1000,
        checkIntervalMs: (config.checkIntervalSeconds || 60) * 1000,
    };
};
